import { memo, useCallback, useEffect, useRef, useState } from 'react';
import parse from 'html-react-parser';
import ReactDOM from 'react-dom';

import { messagesReceivedTimeAtom, renderedMessagesAtom, useAtom, useValue } from '@/store';

import { msToSeconds } from '@/utils';

import { isTestUser } from '@/modules/api';
import { ProductCard } from '@/modules/common';
import { LinkType } from '@/modules/models';

import { MessageTextWrapper, ProcessingTime, ProcessingTimeWrapper } from './MessageText.styles';

interface BubbleProps {
  id?: number;
  links: LinkType[] | null;
  text: string;
  isUserMessage: boolean;
  direction?: 'ltr' | 'rtl';
  mlBeProcessingTime: number | null;
}

export const MessageText = memo<BubbleProps>((props) => {
  const { id, mlBeProcessingTime, links, isUserMessage, text, direction = 'ltr' } = props;

  const isTest = isTestUser();

  const [renderedMessages, setRenderedMessages] = useAtom(renderedMessagesAtom);

  const messagesReceivedTime = useValue(messagesReceivedTimeAtom);

  const [isSingleLine, setIsSingleLine] = useState(false);
  const [productTargets, setProductTargets] = useState<{ element: HTMLElement; handle: string }[]>([]);
  const [renderTime, setRenderTime] = useState(0);

  const bubbleRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const checkMultiline = () => {
      if (!bubbleRef.current) {
        return;
      }

      const computedStyle = window.getComputedStyle(bubbleRef.current);
      const paddingTop = parseFloat(computedStyle.paddingTop);
      const paddingBottom = parseFloat(computedStyle.paddingBottom);
      const lineHeight = parseFloat(computedStyle.lineHeight);
      const contentHeight = bubbleRef.current.clientHeight - paddingTop - paddingBottom;

      setIsSingleLine(contentHeight <= lineHeight * 2.5);
    };

    checkMultiline();
    window.addEventListener('resize', checkMultiline);

    return () => {
      window.removeEventListener('resize', checkMultiline);
    };
  }, []);

  const shouldBreakWords = text.split(' ').length === 1;
  const formattedText = parse(text);

  const getProductFromLinks = useCallback(
    (searchHandle: string) => links?.find(({ handle }) => searchHandle === handle),
    [links]
  );

  useEffect(() => {
    if (bubbleRef.current) {
      const nodes = bubbleRef.current.querySelectorAll('.product');
      const newTargets: { element: HTMLElement; handle: string }[] = [];

      if (!nodes.length && id && !renderedMessages[id]) {
        setRenderedMessages((prev) => ({
          ...prev,
          [id]: true,
        }));
        return;
      }

      nodes.forEach((el) => {
        if (el instanceof HTMLElement) {
          const handle = el.dataset.handle;
          if (handle) {
            newTargets.push({ element: el, handle });
          }
        }
      });

      const isEqual =
        productTargets.length === newTargets.length &&
        productTargets.every((prev, i) => prev.handle === newTargets[i].handle);

      if (!isEqual) {
        setProductTargets(newTargets);
      }
    }
  }, [productTargets, formattedText, setRenderedMessages, id, renderedMessages]);

  useEffect(() => {
    if (!id || renderTime) {
      return;
    }

    const isRendered = Boolean(renderedMessages?.[id]);
    if (isRendered) {
      setRenderTime(new Date().getTime() - messagesReceivedTime?.[id]);
    }
  }, [renderTime, renderedMessages, messagesReceivedTime, id]);

  return (
    <MessageTextWrapper
      ref={bubbleRef}
      isUserMessage={isUserMessage}
      shouldBreakWords={shouldBreakWords}
      isSingleLine={isSingleLine}
      direction={direction}
    >
      {formattedText}
      {productTargets.map(({ element, handle }) => {
        const product = getProductFromLinks(handle);

        if (!product) {
          return null;
        }

        return ReactDOM.createPortal(<ProductCard messageId={id} product={product} bubbleRef={bubbleRef} />, element);
      })}
      {isTest && !isUserMessage && (
        <ProcessingTimeWrapper>
          {mlBeProcessingTime && <ProcessingTime>ML & BE: {msToSeconds(mlBeProcessingTime)}s</ProcessingTime>}
          {Boolean(renderTime) && <ProcessingTime>FE: {msToSeconds(renderTime)}s</ProcessingTime>}
        </ProcessingTimeWrapper>
      )}
    </MessageTextWrapper>
  );
});

MessageText.displayName = 'MessageText';
